import {
  FIELD,
  type FlightState,
  type PilotSeed,
  type Pilot,
  type Enemy,
  type Bullet,
  type FlightAction,
  type FlightCommand,
  type UpgradeId,
  type SideWeapon,
} from "./types";
import { UPGRADES } from "./content-v1";

const clamp = (n: number, low: number, high: number) =>
  Math.max(low, Math.min(high, n));
const q = (n: number) => Math.round(n * 1024) / 1024;
const level = (p: Pilot, id: UpgradeId) => p.levels[id] || 0;
const sides: SideWeapon[] = [
  "wing",
  "orbit",
  "homing",
  "boomerang",
  "split",
  "arc",
];
function random(s: FlightState) {
  let x = s.rng | 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  s.rng = x >>> 0;
  return s.rng / 4294967296;
}
export function createFlight(
  id: string,
  seed: number,
  pilots: PilotSeed[],
): FlightState {
  if (
    !pilots.length ||
    pilots.length > 2 ||
    new Set(pilots.map((p) => p.id)).size !== pilots.length
  )
    throw new Error("战机席位无效");
  return {
    version: 1,
    id,
    mode: pilots.length === 1 ? "solo" : "coop",
    seed: seed >>> 0,
    rng: seed >>> 0 || 1,
    tick: 0,
    nextId: 1,
    phase: "playing",
    pilots: pilots.map((p, i) => ({
      ...JSON.parse(JSON.stringify(p)),
      x: pilots.length === 1 ? 360 : 260 + i * 200,
      y: 1050,
      tx: pilots.length === 1 ? 360 : 260 + i * 200,
      ty: 1050,
      hp: 100,
      maxHp: 100,
      shield: 0,
      invulnerable: 120,
      cooldown: 0,
      fireAt: 0,
      rescue: 0,
      left: false,
      levels: {},
      choices: [],
    })),
    enemies: [],
    bullets: [],
    effects: [],
    level: 1,
    xp: 0,
    bosses: 0,
    kills: 0,
    nextBoss: 10800,
    spawnAt: 30,
    scene: 0,
  };
}
export function validUpgrades(p: Pilot) {
  const occupied = sides.filter((id) => level(p, id) > 0).length;
  return UPGRADES.filter(
    (u) =>
      (!u.cap || level(p, u.id) < u.cap) &&
      (u.group !== "副武器" ||
        (p.unlocked.includes(u.id as SideWeapon) &&
          (level(p, u.id) > 0 || occupied < 3))) &&
      (u.id !== "repair" || (p.hp > 0 && p.hp < p.maxHp)),
  );
}
function offer(s: FlightState) {
  s.phase = "choosing";
  for (const p of s.pilots.filter((p) => !p.left)) {
    const pool = validUpgrades(p);
    p.choices = [];
    while (p.choices.length < 3 && pool.length)
      p.choices.push(pool.splice(Math.floor(random(s) * pool.length), 1)[0].id);
  }
}
function gain(s: FlightState, xp: number) {
  s.xp +=
    xp *
    (1 +
      Math.max(
        0,
        ...s.pilots.filter((p) => !p.left).map((p) => level(p, "experience")),
      ) *
        0.1);
}
export function flightAction(
  s: FlightState,
  id: string,
  a: FlightAction,
): boolean {
  const p = s.pilots.find((p) => p.id === id && !p.left);
  if (!p || s.phase === "ended") return false;
  if (a.kind === "leave") {
    p.left = true;
    p.choices = [];
    p.hp = 0;
    checkEnd(s);
    return true;
  }
  if (a.kind === "end") {
    if (s.mode !== "solo") return false;
    s.phase = "ended";
    s.reason = "主动结束";
    return true;
  }
  if (a.kind === "choose") {
    if (s.phase !== "choosing" || !p.choices.includes(a.upgrade)) return false;
    const id = a.upgrade;
    p.levels[id] = level(p, id) + 1;
    p.choices = [];
    if (id === "health") {
      p.maxHp += 20;
      if (p.hp > 0) p.hp = Math.min(p.maxHp, p.hp + 20);
    }
    if (id === "repair" && p.hp > 0)
      p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.35);
    if (id === "shield") p.shield += 25;
    if (s.pilots.filter((p) => !p.left).every((p) => !p.choices.length))
      s.phase = "playing";
    return true;
  }
  if (s.phase !== "playing" || p.hp <= 0) return false;
  if (a.kind === "move") {
    if (!Number.isFinite(a.x) || !Number.isFinite(a.y)) return false;
    p.tx = q(clamp(a.x, 28, 692));
    p.ty = q(clamp(a.y, 80, 1200));
    return true;
  }
  if (p.cooldown > 0) return false;
  p.cooldown = Math.max(900, 2700 - level(p, "cooldown") * 150);
  s.bullets = s.bullets.filter((b) => b.owner !== "enemy");
  effect(s, p.x, p.y, "ink");
  for (const e of [...s.enemies]) damage(s, e, 120 * attack(p));
  return true;
}
function checkEnd(s: FlightState) {
  if (s.pilots.every((p) => p.left || p.hp <= 0)) {
    s.phase = "ended";
    s.reason = "战机被击落";
  } else if (
    s.phase === "choosing" &&
    s.pilots.filter((p) => !p.left).every((p) => !p.choices.length)
  )
    s.phase = "playing";
}
function attack(p: Pilot) {
  return 1 + level(p, "attack") * 0.15;
}
function effect(
  s: FlightState,
  x: number,
  y: number,
  kind: "hit" | "burst" | "rescue" | "ink",
) {
  s.effects.push({ id: s.nextId++, x, y, age: 0, kind });
  if (s.effects.length > 64) s.effects.shift();
}
function shot(
  s: FlightState,
  owner: string,
  x: number,
  y: number,
  vx: number,
  vy: number,
  damage: number,
  kind = "main",
  radius = 7,
  pierce = 0,
  life = 180,
) {
  const enemy = owner === "enemy";
  const count = s.bullets.filter((b) => (b.owner === "enemy") === enemy).length;
  if (count >= (enemy ? 360 : 260)) return;
  s.bullets.push({
    id: s.nextId++,
    owner,
    kind,
    x: q(x),
    y: q(y),
    vx: q(vx),
    vy: q(vy),
    radius,
    damage,
    life,
    pierce,
    hits: [],
  });
}
function spawn(
  s: FlightState,
  kind: number,
  boss = false,
  x?: number,
  y = -60,
) {
  if (s.enemies.length >= 48) return;
  const time = s.tick / 3600,
    team = s.pilots.filter((p) => !p.left).length > 1 ? 1.65 : 1;
  const scale =
    (1 + time * 0.18 + Math.max(0, time - 10) ** 1.35 * 0.04) * team;
  const hp =
    (boss
      ? 1100 + s.bosses * 450
      : kind === 5
        ? 65
        : kind === 7
          ? 75
          : 22 + kind * 5) * scale;
  s.enemies.push({
    id: s.nextId++,
    kind,
    x: x ?? (boss ? 360 : 45 + random(s) * 630),
    y,
    hp,
    maxHp: hp,
    age: 0,
    fireAt: 60 + Math.floor(random(s) * 70),
    phase: random(s) * Math.PI * 2,
    scale,
    boss,
    radius: boss ? 78 : kind === 5 ? 28 : 22,
  });
}
function damage(s: FlightState, e: Enemy, value: number, p?: Pilot) {
  if (!s.enemies.includes(e)) return;
  e.hp -= value;
  if (p && !e.boss) e.y -= level(p, "push") * 3;
  if (e.hp > 0) {
    effect(s, e.x, e.y, "hit");
    return;
  }
  s.enemies.splice(s.enemies.indexOf(e), 1);
  s.kills++;
  gain(s, e.boss ? 80 : 8 + e.kind * 2);
  effect(s, e.x, e.y, "burst");
  if (e.boss) {
    s.bosses++;
    s.scene = s.bosses % 3;
    s.nextBoss = s.tick + 10800;
    s.bullets = s.bullets.filter((b) => b.owner !== "enemy");
  } else if (e.kind === 6)
    for (const dx of [-28, 28])
      spawn(s, 0, false, clamp(e.x + dx, 30, 690), e.y);
}
function hurt(s: FlightState, p: Pilot, value: number) {
  if (p.hp <= 0 || p.invulnerable > 0 || p.left) return;
  let d = value * (1 - level(p, "armor") * 0.055);
  const absorbed = Math.min(p.shield, d);
  p.shield -= absorbed;
  d -= absorbed;
  p.hp = Math.max(0, p.hp - d);
  p.invulnerable = 60 + level(p, "grace") * 12;
  effect(s, p.x, p.y, p.hp ? "hit" : "burst");
}
function shootPilot(s: FlightState, p: Pilot) {
  if (s.tick < p.fireAt) return;
  const extra = level(p, "lanes"),
    base = p.main === "straight" ? 1 : p.main === "double" ? 2 : 3,
    n = base + extra;
  const interval = Math.max(6, 24 - level(p, "rate") * 2),
    power = (17 / base) * attack(p);
  for (let burst = 0; burst <= level(p, "burst"); burst++)
    for (let i = 0; i < n; i++) {
      const offset = i - (n - 1) / 2,
        angle = p.main === "fan" ? offset * 0.16 : 0,
        speed = 13 + level(p, "velocity") * 2;
      shot(
        s,
        p.id,
        p.x + (p.main === "fan" ? 0 : offset * 19),
        p.y - 32 - burst * 18,
        Math.sin(angle) * speed,
        -Math.cos(angle) * speed,
        power * (random(s) < level(p, "critical") * 0.05 ? 2 : 1),
        "main",
        7 + level(p, "size") * 2,
        level(p, "pierce"),
      );
    }
  p.fireAt = s.tick + interval;
}
function sideFire(s: FlightState, p: Pilot) {
  for (const id of sides) {
    const n = level(p, id);
    if (!n) continue;
    if (id === "orbit") {
      for (let i = 0; i < n; i++) {
        const a = s.tick * 0.045 + (i * Math.PI * 2) / n,
          x = p.x + Math.cos(a) * 68,
          y = p.y + Math.sin(a) * 68;
        for (const b of s.bullets)
          if (b.owner === "enemy" && Math.hypot(b.x - x, b.y - y) < 19)
            b.life = 0;
        if (s.tick % 12 === 0) {
          shot(s, p.id, x, y, 0, 0, 9 * n * attack(p), "orbit", 14, 3, 2);
        }
      }
      continue;
    }
    if (s.tick % Math.max(18, 85 - n * 10) !== 0) continue;
    if (id === "arc") {
      const nearest = [...s.enemies]
        .sort(
          (a, b) =>
            Math.hypot(a.x - p.x, a.y - p.y) - Math.hypot(b.x - p.x, b.y - p.y),
        )
        .filter((e) => Math.hypot(e.x - p.x, e.y - p.y) < 500)
        .slice(0, 1 + n);
      for (const e of nearest) {
        shot(
          s,
          p.id,
          p.x,
          p.y,
          (e.x - p.x) / 10,
          (e.y - p.y) / 10,
          12 * n * attack(p),
          "arc",
          12,
          0,
          12,
        );
      }
      continue;
    }
    if (id === "wing") {
      for (const dx of [-54, 54])
        shot(s, p.id, p.x + dx, p.y - 8, 0, -12, 10 * n * attack(p), "wing");
      continue;
    }
    shot(
      s,
      p.id,
      p.x,
      p.y - 30,
      0,
      -10,
      (id === "split" ? 18 : 13) * n * attack(p),
      id,
      10,
      id === "boomerang" ? 8 : 0,
      id === "boomerang" ? 160 : 200,
    );
  }
}
function enemyFire(s: FlightState, e: Enemy) {
  if (e.age < e.fireAt) return;
  const p = s.pilots.filter((p) => !p.left && p.hp > 0)[
    Math.floor(random(s) * s.pilots.filter((p) => !p.left && p.hp > 0).length)
  ];
  if (!p) return;
  const speed = Math.min(6, 2.4 + s.tick / 21600),
    power = 13 + Math.min(35, s.tick / 10800) * 2;
  const aimed = Math.atan2(p.y - e.y, p.x - e.x);
  const n = e.boss
    ? e.kind === 1
      ? 14
      : e.kind === 2
        ? 9
        : 7
    : e.kind === 4
      ? 8
      : e.kind === 2
        ? 3
        : 1;
  for (let i = 0; i < n; i++) {
    const radial = e.kind === 4 || (e.boss && e.kind === 1),
      a = radial
        ? (i * Math.PI * 2) / n + e.age * 0.012
        : aimed + (i - (n - 1) / 2) * 0.18;
    shot(
      s,
      "enemy",
      e.x,
      e.y,
      Math.cos(a) * speed,
      Math.sin(a) * speed,
      power,
      e.boss ? "boss" : "enemy",
      e.boss ? 9 : 7,
    );
  }
  if (e.kind === 7 || (e.boss && e.kind === 2))
    for (const dx of [-55, 55])
      spawn(s, 0, false, clamp(e.x + dx, 30, 690), e.y + 50);
  e.fireAt =
    e.age + Math.max(30, (e.boss ? 75 : 170) - Math.floor(s.tick / 3600) * 4);
}
/** Swept segment collision prevents fast shots crossing a target between ticks. */
export function segmentDistance(
  x: number,
  y: number,
  nx: number,
  ny: number,
  cx: number,
  cy: number,
) {
  const dx = nx - x,
    dy = ny - y,
    d = dx * dx + dy * dy,
    t = d ? clamp(((cx - x) * dx + (cy - y) * dy) / d, 0, 1) : 0;
  return Math.hypot(x + dx * t - cx, y + dy * t - cy);
}
export function stepFlight(s: FlightState) {
  if (s.phase !== "playing") return;
  s.tick++;
  for (const p of s.pilots) {
    if (p.left) continue;
    if (p.hp <= 0) {
      const near = s.pilots.some(
        (other) =>
          other !== p &&
          !other.left &&
          other.hp > 0 &&
          Math.hypot(other.x - p.x, other.y - p.y) < 85,
      );
      p.rescue = near ? p.rescue + 1 : 0;
      if (p.rescue >= 180) {
        p.hp = p.maxHp * 0.5;
        p.invulnerable = 120;
        p.rescue = 0;
        effect(s, p.x, p.y, "rescue");
      }
      continue;
    }
    const dx = p.tx - p.x,
      dy = p.ty - p.y,
      d = Math.hypot(dx, dy),
      speed = 6 * (1 + level(p, "speed") * 0.08);
    if (d) {
      p.x = q(p.x + dx * Math.min(1, speed / d));
      p.y = q(p.y + dy * Math.min(1, speed / d));
    }
    p.invulnerable = Math.max(0, p.invulnerable - 1);
    p.cooldown = Math.max(0, p.cooldown - 1);
    p.hp = Math.min(p.maxHp, p.hp + level(p, "regen") / 180);
    shootPilot(s, p);
    sideFire(s, p);
  }
  if (s.tick >= s.nextBoss && !s.enemies.some((e) => e.boss)) {
    spawn(s, s.bosses % 3, true);
    s.nextBoss = Number.MAX_SAFE_INTEGER;
  }
  if (s.tick >= s.spawnAt) {
    const available = Math.min(8, 2 + Math.floor(s.tick / 1800));
    spawn(s, Math.floor(random(s) * available));
    s.spawnAt = s.tick + Math.max(15, 85 - Math.floor(s.tick / 1200) * 4);
  }
  for (const e of [...s.enemies]) {
    e.age++;
    if (e.boss) {
      e.y = q(Math.min(210, e.y + 1.4));
      e.x = q(360 + Math.sin(e.age * 0.009) * 220);
      if (e.kind === 0 && e.age % 480 > 320)
        e.y = q(210 + Math.sin((((e.age % 480) - 320) / 160) * Math.PI) * 340);
    } else {
      e.y = q(e.y + 1.4 + Math.min(2.5, s.tick / 18000));
      if (e.kind === 1 || e.kind === 2)
        e.x = q(
          clamp(
            e.x +
              Math.sin(e.age * (e.kind === 1 ? 0.065 : 0.018) + e.phase) * 2.5,
            25,
            695,
          ),
        );
    }
    if (e.kind !== 0) enemyFire(s, e);
    for (const p of s.pilots)
      if (Math.hypot(p.x - e.x, p.y - e.y) < e.radius + FIELD.core)
        hurt(s, p, 28);
    if (e.y > 1370) s.enemies.splice(s.enemies.indexOf(e), 1);
  }
  const bullets = [...s.bullets];
  for (const b of bullets) {
    if (b.life <= 0) continue;
    if (b.kind === "homing") {
      const target = [...s.enemies].sort(
        (a, c) =>
          Math.hypot(a.x - b.x, a.y - b.y) - Math.hypot(c.x - b.x, c.y - b.y),
      )[0];
      if (target) {
        const a = Math.atan2(target.y - b.y, target.x - b.x);
        b.vx = q(b.vx * 0.85 + Math.cos(a) * 1.8);
        b.vy = q(b.vy * 0.85 + Math.sin(a) * 1.8);
      }
    }
    if (b.kind === "boomerang") b.vy = q(b.vy + 0.14);
    const nx = q(b.x + b.vx),
      ny = q(b.y + b.vy);
    if (b.owner === "enemy") {
      for (const p of s.pilots)
        if (
          !p.left &&
          p.hp > 0 &&
          segmentDistance(b.x, b.y, nx, ny, p.x, p.y) < FIELD.core + b.radius
        ) {
          hurt(s, p, b.damage);
          b.life = 0;
          break;
        }
    } else
      for (const e of [...s.enemies]) {
        if (
          b.hits.includes(e.id) ||
          segmentDistance(b.x, b.y, nx, ny, e.x, e.y) >= e.radius + b.radius
        )
          continue;
        b.hits.push(e.id);
        damage(
          s,
          e,
          b.damage,
          s.pilots.find((p) => p.id === b.owner),
        );
        if (b.kind === "split")
          for (let i = 0; i < 4; i++)
            shot(
              s,
              b.owner,
              e.x,
              e.y,
              Math.cos((i * Math.PI) / 2) * 7,
              Math.sin((i * Math.PI) / 2) * 7,
              b.damage * 0.45,
              "fragment",
              6,
            );
        if (b.pierce-- <= 0) {
          b.life = 0;
          break;
        }
      }
    b.x = nx;
    b.y = ny;
    b.life--;
  }
  s.bullets = s.bullets.filter(
    (b) => b.life > 0 && b.x > -100 && b.x < 820 && b.y > -150 && b.y < 1400,
  );
  for (const e of s.effects) e.age++;
  s.effects = s.effects.filter((e) => e.age < (e.kind === "ink" ? 42 : 25));
  checkEnd(s);
  if (s.phase === "playing" && s.xp >= 30 + s.level * 16) {
    s.xp -= 30 + s.level * 16;
    s.level++;
    offer(s);
  }
}
export function replayCommands(
  s: FlightState,
  commands: FlightCommand[],
  until: number,
) {
  if (!Number.isSafeInteger(until) || until < s.tick || until - s.tick > 3600)
    throw new Error("日志分块时间范围无效");
  let last = s.tick;
  for (const command of commands) {
    if (
      !Number.isSafeInteger(command.tick) ||
      command.tick < last ||
      command.tick > until
    )
      throw new Error("操作时间顺序无效");
    while (s.tick < command.tick) {
      if (s.phase !== "playing") throw new Error("暂停或结束后不能推进战斗");
      stepFlight(s);
    }
    if (!flightAction(s, command.player, command.action))
      throw new Error("战斗操作无效");
    last = command.tick;
  }
  while (s.tick < until) {
    if (s.phase !== "playing")
      throw new Error("战斗日志缺少升级选择或包含结束后的时间");
    stepFlight(s);
  }
  return s;
}
